import { useMemo } from 'react';
import { Button, Card, CardBody, Icon } from '@repo/ui';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils';
import { sumBy } from 'lodash';

import { BillsTable } from './BillsTable';
import { ReceiptsTable } from './ReceiptsTable';
import { WriteoffsTable } from './WriteoffsTable';
import { TrustTable } from './TrustTable';
import { LedgerReportForm, LedgerType } from '../../types';
import { useArLedgerExportCsv, useGenerateLegerReport } from '../../queries';
import { LedgerTypeMapLabel } from '../../constants';
import { ArLedgerReportType } from '@repo/types';

interface MatterLedgerReportProps {
  formValues: LedgerReportForm;
}

export default function MatterLedgerReport({ formValues }: MatterLedgerReportProps) {
  const { data, isLoading, error } = useGenerateLegerReport(formValues);

  const report = useMemo(() => data?.data, [data]);

  const { isFetching: isExporting, refetch: exportCsv } = useArLedgerExportCsv(formValues);

  const fieldsMap: Record<LedgerType, { label: string; value: string | undefined }[]> = {
    [ArLedgerReportType.CLIENT]: [{ label: 'Client', value: report?.clientName }],
    [ArLedgerReportType.MATTER]: [
      { label: 'Client', value: report?.clientName },
      { label: 'Originating Attorney', value: report?.originatingAttorneyName },
      { label: 'Responsible Attorney', value: report?.responsibleAttorneyName },
    ],
    [ArLedgerReportType.ORIGINATING_ATTORNEY]: [
      { label: 'Originating Attorney', value: report?.originatingAttorneyName },
    ],
    [ArLedgerReportType.RESPONSIBLE_ATTORNEY]: [
      { label: 'Responsible Attorney', value: report?.responsibleAttorneyName },
    ],
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between py-2">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold text-xtnd-dark dark:text-xtnd-light">
            {formValues.ledgerType ? LedgerTypeMapLabel[formValues.ledgerType] : ''} Ledger Report
          </h2>
          <span className="text-sm text-xtnd-dark dark:text-xtnd-light">
            {(formValues.dateRange.from
              ? dateUtil.formatDate(formValues.dateRange.from, dateUtil.DateFormat.US)
              : 'N/A') +
              ' to ' +
              (formValues.dateRange.to
                ? dateUtil.formatDate(formValues.dateRange.to, dateUtil.DateFormat.US)
                : 'N/A')}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="primary"
            size="sm"
            disabled={isExporting || isLoading}
            loading={isExporting}
            leftIcon={<Icon name="FaFileAlt" />}
            onClick={() => exportCsv()}
          >
            Export to Excel
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Icon name="FaPrint" />}>
            Print/PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardBody>
          {isLoading && (
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <div
                      className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 h-4 w-24`}
                    />
                    <div
                      className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 h-5 w-full`}
                    />
                  </div>
                ))}
              </div>

              <div className="flex flex-col mt-2 gap-1">
                <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 h-4 w-32`} />
                <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 h-5 w-full`} />
              </div>
            </div>
          )}

          {!isLoading && (
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">Run Date</p>
                  <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {formValues.runDate
                      ? dateUtil.formatDate(formValues.runDate, dateUtil.DateFormat.US)
                      : dateUtil.formatDate(new Date(), dateUtil.DateFormat.US)}
                  </span>
                </div>

                <div className="flex flex-col">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">Date Range</p>
                  <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {(formValues.dateRange.from
                      ? dateUtil.formatDate(formValues.dateRange.from, dateUtil.DateFormat.US)
                      : 'N/A') +
                      ' to ' +
                      (formValues.dateRange.to
                        ? dateUtil.formatDate(formValues.dateRange.to, dateUtil.DateFormat.US)
                        : 'N/A')}
                  </span>
                </div>

                {formValues.ledgerType &&
                  fieldsMap[formValues.ledgerType]?.map(field => (
                    <div key={field.label} className="flex flex-col">
                      <p className="text-sm text-xtnd-dark dark:text-xtnd-light">{field.label}</p>
                      <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light break-words whitespace-normal block">
                        {field.value || 'N/A'}
                      </span>
                    </div>
                  ))}

                <div className="flex flex-col">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">Total AR</p>
                  <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {numberUtil.formatCurrency(report?.summary?.arAgings?.arTotal || 0)}
                  </span>
                </div>

                <div className="flex flex-col">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">Total WIP</p>
                  <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {numberUtil.formatCurrency(report?.summary?.wipTotal || 0)}
                  </span>
                </div>

                <div className="flex flex-col">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">Total Trust</p>
                  <span className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {numberUtil.formatCurrency(
                      sumBy(report?.trusts ?? [], item => item?.value ?? 0)
                    )}
                  </span>
                </div>
              </div>

              {report?.isSIR && (
                <div className="flex flex-col mt-2">
                  <p className="text-sm text-xtnd-dark dark:text-xtnd-light">SIR Deductible</p>
                  <span className="break-words whitespace-normal block text-sm font-bold text-xtnd-dark dark:text-xtnd-light">
                    {report.clientDeductible}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
      <BillsTable data={report?.bills || []} isLoading={isLoading} error={error} />
      <ReceiptsTable data={report?.receipts || []} isLoading={isLoading} error={error} />
      <WriteoffsTable data={report?.writeoffs || []} isLoading={isLoading} error={error} />
      <TrustTable data={report?.trusts || []} isLoading={isLoading} error={error} />
    </div>
  );
}
